import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const CoachEvidenceSchema = Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]);

export const CoachProfileStatusSchema = Type.Union([
  Type.Literal("ENABLED"),
  Type.Literal("DISABLED"),
]);
export type CoachProfileStatus = Static<typeof CoachProfileStatusSchema>;

export const CoachLevelSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    code: Type.String({ minLength: 1, maxLength: 32, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    enabled: Type.Boolean(),
    version: Type.Integer({ minimum: 1 }),
    evidence: CoachEvidenceSchema,
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CoachLevel = Static<typeof CoachLevelSchema>;

export const CoachProfileSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachNumber: Type.String({
      minLength: 1,
      maxLength: 32,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
    }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    avatarUrl: Type.Union([Type.Null(), Type.String({ minLength: 1, maxLength: 500 })]),
    controlledPhone: Type.Union([Type.String({ minLength: 5, maxLength: 32 }), Type.Null()]),
    levelId: Type.String({ minLength: 1, maxLength: 64 }),
    status: CoachProfileStatusSchema,
    version: Type.Integer({ minimum: 1 }),
    evidence: CoachEvidenceSchema,
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CoachProfile = Static<typeof CoachProfileSchema>;

export const CoachProfilePublicSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachNumber: Type.String({ minLength: 1, maxLength: 32 }),
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    avatarUrl: Type.Union([Type.Null(), Type.String({ minLength: 1, maxLength: 500 })]),
    levelId: Type.String({ minLength: 1, maxLength: 64 }),
    status: CoachProfileStatusSchema,
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachProfilePublic = Static<typeof CoachProfilePublicSchema>;

export const CoachBillingModeSchema = Type.Union([
  Type.Literal("HOURLY"),
  Type.Literal("PER_SESSION"),
]);
export type CoachBillingMode = Static<typeof CoachBillingModeSchema>;

export const CoachCommissionModeSchema = Type.Union([
  Type.Literal("PERCENTAGE"),
  Type.Literal("FIXED"),
  Type.Literal("MONTHLY_TIERED"),
]);
export type CoachCommissionMode = Static<typeof CoachCommissionModeSchema>;

export const CoachTierCalculationModeSchema = Type.Union([
  Type.Literal("SEGMENTED_PROGRESSIVE"),
  Type.Literal("WHOLE_MONTH"),
]);
export type CoachTierCalculationMode = Static<typeof CoachTierCalculationModeSchema>;

export const CoachCommissionTierSchema = Type.Object(
  {
    fromMinute: Type.Integer({ minimum: 0, maximum: 100_000 }),
    toMinute: Type.Union([Type.Integer({ minimum: 1, maximum: 100_000 }), Type.Null()]),
    rateBps: Type.Integer({ minimum: 0, maximum: 10_000 }),
  },
  { additionalProperties: false },
);
export type CoachCommissionTier = Static<typeof CoachCommissionTierSchema>;

export const CoachPriceSourceSchema = Type.Union([
  Type.Literal("LEVEL_DEFAULT"),
  Type.Literal("COACH_OVERRIDE"),
]);
export type CoachPriceSource = Static<typeof CoachPriceSourceSchema>;

export const CoachPriceOverrideSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 96 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    serviceRuleId: Type.String({ minLength: 1, maxLength: 64 }),
    serviceTypeCode: Type.String({ minLength: 1, maxLength: 32 }),
    priceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    enabled: Type.Boolean(),
    effectiveMonth: Type.String({ pattern: "^\\d{4}-\\d{2}$" }),
    version: Type.Integer({ minimum: 1 }),
    changeReason: Type.String({ minLength: 2, maxLength: 500 }),
    createdBy: Type.String({ minLength: 1, maxLength: 128 }),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
    supersededAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachPriceOverride = Static<typeof CoachPriceOverrideSchema>;

export const CoachServiceRuleSnapshotSchema = Type.Object(
  {
    ruleId: Type.String({ minLength: 1, maxLength: 64 }),
    version: Type.Integer({ minimum: 1 }),
    levelId: Type.String({ minLength: 1, maxLength: 64 }),
    serviceTypeCode: Type.String({
      minLength: 1,
      maxLength: 32,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
    }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    billingMode: CoachBillingModeSchema,
    minimumMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    billingStepMinutes: Type.Integer({ minimum: 1, maximum: 1_440 }),
    priceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    commissionMode: CoachCommissionModeSchema,
    commissionRateBps: Type.Integer({ minimum: 0, maximum: 10_000 }),
    fixedCommissionFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    tierCalculationMode: Type.Optional(CoachTierCalculationModeSchema),
    commissionTiers: Type.Optional(
      Type.Array(CoachCommissionTierSchema, { minItems: 1, maxItems: 24 }),
    ),
    effectiveMonth: Type.Optional(Type.String({ pattern: "^\\d{4}-\\d{2}$" })),
    commissionRuleVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    changeReason: Type.Optional(Type.String({ minLength: 2, maxLength: 500 })),
    levelNameSnapshot: Type.Optional(Type.String({ minLength: 1, maxLength: 80 })),
    defaultPriceFen: Type.Optional(Type.Integer({ minimum: 0, maximum: 100_000_000 })),
    priceSource: Type.Optional(CoachPriceSourceSchema),
    priceOverrideId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    priceOverrideVersion: Type.Optional(Type.Union([Type.Integer({ minimum: 1 }), Type.Null()])),
    priceEffectiveMonth: Type.Optional(Type.String({ pattern: "^\\d{4}-\\d{2}$" })),
    operationSource: Type.Optional(
      Type.Union([
        Type.Literal("FRONTDESK"),
        Type.Literal("COACH_WORKBENCH"),
        Type.Literal("OFFLINE_REPLAY"),
        Type.Literal("SYSTEM"),
      ]),
    ),
    effectiveAt: Type.String({ format: "date-time" }),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachServiceRuleSnapshot = Static<typeof CoachServiceRuleSnapshotSchema>;

export const CoachServiceRuleSchema = Type.Object(
  {
    ...CoachServiceRuleSnapshotSchema.properties,
    enabled: Type.Boolean(),
    supersededAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
    createdBy: Type.String({ minLength: 1, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CoachServiceRule = Static<typeof CoachServiceRuleSchema>;

export const CoachAssignableServiceOptionSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    ruleId: Type.String({ minLength: 1, maxLength: 64 }),
    version: Type.Integer({ minimum: 1 }),
    levelId: Type.String({ minLength: 1, maxLength: 64 }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    priceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    pricingToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type CoachAssignableServiceOption = Static<typeof CoachAssignableServiceOptionSchema>;

export const CoachAssignmentTypeSchema = Type.Union([
  Type.Literal("ROTATION"),
  Type.Literal("REQUESTED"),
]);
export type CoachAssignmentType = Static<typeof CoachAssignmentTypeSchema>;

export const CoachServiceAssignmentFundingPreviewSchema = Type.Object(
  {
    paymentTiming: Type.Union([Type.Literal("POSTPAID"), Type.Literal("PREPAID")]),
    paymentMethod: Type.Union([Type.Literal("MEMBER_BALANCE"), Type.Null()]),
    memberId: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    memberNumber: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    memberDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    availableBalanceFen: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    estimatedInitialServiceFeeFen: Type.Integer({
      minimum: 0,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    estimatedBalanceAfterInitialFeeFen: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    balanceOutcome: Type.Union([
      Type.Literal("NOT_APPLICABLE"),
      Type.Literal("SUFFICIENT_FOR_INITIAL_CHARGE"),
      Type.Literal("INSUFFICIENT_FOR_INITIAL_CHARGE"),
    ]),
    memberVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    walletVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type CoachServiceAssignmentFundingPreview = Static<
  typeof CoachServiceAssignmentFundingPreviewSchema
>;

export const CoachServiceAssignmentPreviewRequestSchema = Type.Object(
  {
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    coachId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    expectedCoachId: Type.String({ minLength: 1, maxLength: 64 }),
    assignmentType: CoachAssignmentTypeSchema,
    serviceRuleId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedPriceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    pricingToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type CoachServiceAssignmentPreviewRequest = Static<
  typeof CoachServiceAssignmentPreviewRequestSchema
>;

export const CoachServiceAssignmentPreviewSchema = Type.Object(
  {
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    orderId: Type.String({ minLength: 1, maxLength: 128 }),
    orderAggregateVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    assignmentType: CoachAssignmentTypeSchema,
    serviceRuleId: Type.String({ minLength: 1, maxLength: 64 }),
    serviceRuleVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    billingMode: CoachBillingModeSchema,
    minimumMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    billingStepMinutes: Type.Integer({ minimum: 1, maximum: 1_440 }),
    priceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    pricingToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    funding: CoachServiceAssignmentFundingPreviewSchema,
    previewedAt: Type.String({ format: "date-time" }),
    expiresAt: Type.String({ format: "date-time" }),
    previewToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type CoachServiceAssignmentPreview = Static<typeof CoachServiceAssignmentPreviewSchema>;

export function isCoachServiceAssignmentPreview(
  value: unknown,
): value is CoachServiceAssignmentPreview {
  if (!Value.Check(CoachServiceAssignmentPreviewSchema, value)) return false;
  const preview = value as CoachServiceAssignmentPreview;
  const previewedAt = Date.parse(preview.previewedAt);
  const expiresAt = Date.parse(preview.expiresAt);
  const funding = preview.funding;
  const postpaidValid =
    funding.paymentTiming === "POSTPAID" &&
    funding.paymentMethod === null &&
    funding.memberId === null &&
    funding.memberNumber === null &&
    funding.memberDisplayName === null &&
    funding.availableBalanceFen === null &&
    funding.estimatedBalanceAfterInitialFeeFen === null &&
    funding.balanceOutcome === "NOT_APPLICABLE" &&
    funding.memberVersion === null &&
    funding.walletVersion === null;
  const prepaidValid =
    funding.paymentTiming === "PREPAID" &&
    funding.paymentMethod === "MEMBER_BALANCE" &&
    funding.memberId !== null &&
    funding.memberNumber !== null &&
    funding.memberDisplayName !== null &&
    funding.availableBalanceFen !== null &&
    funding.memberVersion !== null &&
    funding.walletVersion !== null &&
    funding.estimatedBalanceAfterInitialFeeFen ===
      Math.max(0, funding.availableBalanceFen - funding.estimatedInitialServiceFeeFen) &&
    funding.balanceOutcome ===
      (funding.availableBalanceFen >= funding.estimatedInitialServiceFeeFen
        ? "SUFFICIENT_FOR_INITIAL_CHARGE"
        : "INSUFFICIENT_FOR_INITIAL_CHARGE");
  return (
    Number.isFinite(previewedAt) &&
    Number.isFinite(expiresAt) &&
    expiresAt > previewedAt &&
    (postpaidValid || prepaidValid)
  );
}

export const CoachShiftStatusSchema = Type.Union([
  Type.Literal("NOT_CHECKED_IN"),
  Type.Literal("AVAILABLE"),
  Type.Literal("QUEUED"),
  Type.Literal("SERVING"),
  Type.Literal("PAUSED"),
  Type.Literal("CHECKED_OUT"),
]);
export type CoachShiftStatus = Static<typeof CoachShiftStatusSchema>;

export const CoachShiftSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    employmentId: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    scheduledStartAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    scheduledEndAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    checkedInAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    checkedOutAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    status: CoachShiftStatusSchema,
    version: Type.Integer({ minimum: 1 }),
    offlineOrigin: Type.Boolean(),
    syncState: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("SYNCED"),
      Type.Literal("CONFLICT"),
      Type.Literal("FAILED"),
      Type.Literal("ISOLATED"),
    ]),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CoachShift = Static<typeof CoachShiftSchema>;

export const CoachQueueEntryStatusSchema = Type.Union([
  Type.Literal("QUEUED"),
  Type.Literal("PAUSED"),
]);
export type CoachQueueEntryStatus = Static<typeof CoachQueueEntryStatusSchema>;

export const CoachQueueEntrySchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    position: Type.Integer({ minimum: 1 }),
    status: CoachQueueEntryStatusSchema,
    joinedAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
    lastReason: Type.Union([Type.String({ maxLength: 500 }), Type.Null()]),
    version: Type.Integer({ minimum: 1 }),
    offlineOrigin: Type.Boolean(),
    syncState: Type.Optional(
      Type.Union([
        Type.Literal("PENDING"),
        Type.Literal("SYNCED"),
        Type.Literal("CONFLICT"),
        Type.Literal("FAILED"),
        Type.Literal("ISOLATED"),
      ]),
    ),
  },
  { additionalProperties: false },
);
export type CoachQueueEntry = Static<typeof CoachQueueEntrySchema>;

export const CoachServiceStatusSchema = Type.Union([
  Type.Literal("ASSIGNED"),
  Type.Literal("ACTIVE"),
  Type.Literal("COMPLETED"),
  Type.Literal("CANCELLED"),
  Type.Literal("EARLY_ENDED"),
  Type.Literal("ABNORMAL_TERMINATED"),
  Type.Literal("REVERSED"),
]);
export type CoachServiceStatus = Static<typeof CoachServiceStatusSchema>;

export const CoachServiceRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    shiftId: Type.String({ minLength: 1, maxLength: 64 }),
    sessionId: Type.String({ minLength: 1, maxLength: 64 }),
    orderId: Type.String({ minLength: 1, maxLength: 64 }),
    tableId: Type.String({ minLength: 1, maxLength: 64 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    assignmentType: CoachAssignmentTypeSchema,
    status: CoachServiceStatusSchema,
    assignedAt: Type.String({ format: "date-time" }),
    startedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    endedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    billedMinutes: Type.Integer({ minimum: 0 }),
    effectiveSeconds: Type.Optional(Type.Integer()),
    commissionMonth: Type.Optional(Type.String({ pattern: "^\\d{4}-\\d{2}$" })),
    ruleSnapshot: CoachServiceRuleSnapshotSchema,
    accruedServiceFeeFen: Type.Integer(),
    commissionBaseFen: Type.Integer(),
    commissionFen: Type.Integer(),
    originalServiceId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    replacementServiceId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    reason: Type.Union([Type.String({ minLength: 2, maxLength: 500 }), Type.Null()]),
    actorId: Type.String({ minLength: 1, maxLength: 128 }),
    authorizerId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    version: Type.Integer({ minimum: 1 }),
    offlineOrigin: Type.Boolean(),
    syncState: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("SYNCED"),
      Type.Literal("CONFLICT"),
      Type.Literal("FAILED"),
      Type.Literal("ISOLATED"),
    ]),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CoachServiceRecord = Static<typeof CoachServiceRecordSchema>;

export const CoachServiceReplacementResultSchema = Type.Object(
  {
    previousService: CoachServiceRecordSchema,
    replacementService: CoachServiceRecordSchema,
  },
  { additionalProperties: false },
);
export type CoachServiceReplacementResult = Static<typeof CoachServiceReplacementResultSchema>;

export const CoachServiceReplacementOperatorResultSchema = Type.Object(
  {
    previousService: Type.Object(
      {
        id: Type.String({ minLength: 1, maxLength: 64 }),
        coachId: Type.String({ minLength: 1, maxLength: 64 }),
        businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
        status: Type.Literal("EARLY_ENDED"),
        startedAt: Type.String({ format: "date-time" }),
        endedAt: Type.String({ format: "date-time" }),
        billedMinutes: Type.Integer({ minimum: 0 }),
        serviceFeeFen: Type.Integer({ minimum: 0 }),
        replacementServiceId: Type.String({ minLength: 1, maxLength: 64 }),
        version: Type.Integer({ minimum: 1 }),
      },
      { additionalProperties: false },
    ),
    replacementService: Type.Object(
      {
        id: Type.String({ minLength: 1, maxLength: 64 }),
        coachId: Type.String({ minLength: 1, maxLength: 64 }),
        businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
        status: Type.Literal("ACTIVE"),
        assignedAt: Type.String({ format: "date-time" }),
        startedAt: Type.String({ format: "date-time" }),
        originalServiceId: Type.String({ minLength: 1, maxLength: 64 }),
        version: Type.Literal(2),
      },
      { additionalProperties: false },
    ),
    commissionFen: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type CoachServiceReplacementOperatorResult = Static<
  typeof CoachServiceReplacementOperatorResultSchema
>;

export const CoachOrderLineSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    serviceId: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    assignmentType: CoachAssignmentTypeSchema,
    serviceTypeCode: Type.String({ minLength: 1, maxLength: 32 }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    billedMinutes: Type.Integer({ minimum: 0 }),
    amountFen: Type.Integer(),
    originalLineId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachOrderLine = Static<typeof CoachOrderLineSchema>;

export const CoachAuditRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    action: Type.String({ minLength: 1, maxLength: 80 }),
    objectType: Type.String({ minLength: 1, maxLength: 80 }),
    objectId: Type.String({ minLength: 1, maxLength: 64 }),
    actorId: Type.String({ minLength: 1, maxLength: 128 }),
    authorizerId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    reason: Type.Union([Type.String({ minLength: 2, maxLength: 500 }), Type.Null()]),
    occurredAt: Type.String({ format: "date-time" }),
    offlineOrigin: Type.Boolean(),
    details: Type.Record(Type.String(), Type.Unknown()),
  },
  { additionalProperties: false },
);
export type CoachAuditRecord = Static<typeof CoachAuditRecordSchema>;

export const CoachSummaryRowSchema = Type.Object(
  {
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    coachId: Type.String({ minLength: 1 }),
    coachNumber: Type.String({ minLength: 1 }),
    coachDisplayName: Type.String({ minLength: 1 }),
    serviceTypeCode: Type.String({ minLength: 1 }),
    serviceTypeName: Type.String({ minLength: 1 }),
    assignmentType: CoachAssignmentTypeSchema,
    serviceCount: Type.Integer({ minimum: 0 }),
    serviceMinutes: Type.Integer({ minimum: 0 }),
    grossServiceFeeFen: Type.Integer(),
    adjustmentFen: Type.Integer(),
    netServiceFeeFen: Type.Integer(),
    commissionBaseFen: Type.Integer(),
    commissionFen: Type.Integer(),
    offlineServiceCount: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type CoachSummaryRow = Static<typeof CoachSummaryRowSchema>;

export const CoachMonthlyCommissionSegmentSchema = Type.Object(
  {
    fromMinute: Type.Integer({ minimum: 0 }),
    toMinute: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    effectiveSeconds: Type.Integer({ minimum: 0 }),
    netServiceFeeFen: Type.Integer({ minimum: 0 }),
    rateBps: Type.Integer({ minimum: 0, maximum: 10_000 }),
    commissionFen: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type CoachMonthlyCommissionSegment = Static<typeof CoachMonthlyCommissionSegmentSchema>;

export const CoachMonthlyCommissionSummarySchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    month: Type.String({ pattern: "^\\d{4}-(0[1-9]|1[0-2])$" }),
    status: Type.Union([Type.Literal("ESTIMATED"), Type.Literal("SETTLED")]),
    calculationMode: Type.Union([CoachTierCalculationModeSchema, Type.Null()]),
    effectiveSeconds: Type.Integer({ minimum: 0 }),
    netServiceFeeFen: Type.Integer({ minimum: 0 }),
    commissionFen: Type.Integer({ minimum: 0 }),
    currentRateBps: Type.Union([Type.Integer({ minimum: 0, maximum: 10_000 }), Type.Null()]),
    nextTierFromMinute: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    minutesToNextTier: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    segments: Type.Array(CoachMonthlyCommissionSegmentSchema, { maxItems: 1_000 }),
    ruleVersions: Type.Array(
      Type.Object(
        {
          ruleId: Type.String({ minLength: 1, maxLength: 64 }),
          version: Type.Integer({ minimum: 1 }),
        },
        { additionalProperties: false },
      ),
      { maxItems: 1_000 },
    ),
    recalculatedAt: Type.String({ format: "date-time" }),
    settledAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    settledBy: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    settlementReason: Type.Union([Type.String({ minLength: 2, maxLength: 500 }), Type.Null()]),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachMonthlyCommissionSummary = Static<typeof CoachMonthlyCommissionSummarySchema>;

export const CoachMonthlyCommissionReadModelSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    month: Type.String({ pattern: "^\\d{4}-(0[1-9]|1[0-2])$" }),
    status: Type.Union([Type.Literal("ESTIMATED"), Type.Literal("SETTLED")]),
    calculationMode: Type.Union([CoachTierCalculationModeSchema, Type.Null()]),
    effectiveSeconds: Type.Integer({ minimum: 0 }),
    netServiceFeeFen: Type.Integer({ minimum: 0 }),
    commissionFen: Type.Integer({ minimum: 0 }),
    currentRateBps: Type.Union([Type.Integer({ minimum: 0, maximum: 10_000 }), Type.Null()]),
    minutesToNextTier: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    segments: Type.Array(CoachMonthlyCommissionSegmentSchema, { maxItems: 1_000 }),
  },
  { additionalProperties: false },
);
export type CoachMonthlyCommissionReadModel = Static<typeof CoachMonthlyCommissionReadModelSchema>;

export const CoachRuntimeSnapshotSchema = Type.Object(
  {
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    profiles: Type.Array(CoachProfilePublicSchema),
    shifts: Type.Array(CoachShiftSchema),
    queue: Type.Array(CoachQueueEntrySchema),
    services: Type.Array(CoachServiceRecordSchema),
    pendingSyncRecords: Type.Integer({ minimum: 0 }),
    conflictRecords: Type.Integer({ minimum: 0 }),
    reviewRecords: Type.Integer({ minimum: 0 }),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachRuntimeSnapshot = Static<typeof CoachRuntimeSnapshotSchema>;

const CoachRuntimeSyncStateSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("SYNCED"),
  Type.Literal("CONFLICT"),
  Type.Literal("FAILED"),
  Type.Literal("ISOLATED"),
]);

export const CoachRuntimeShiftReadModelSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    status: CoachShiftStatusSchema,
    checkedInAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    syncState: CoachRuntimeSyncStateSchema,
  },
  { additionalProperties: false },
);
export type CoachRuntimeShiftReadModel = Static<typeof CoachRuntimeShiftReadModelSchema>;

export const CoachRuntimeQueueReadModelSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    position: Type.Integer({ minimum: 1 }),
    status: CoachQueueEntryStatusSchema,
    joinedAt: Type.String({ format: "date-time" }),
    offlineOrigin: Type.Boolean(),
    syncState: Type.Union([CoachRuntimeSyncStateSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type CoachRuntimeQueueReadModel = Static<typeof CoachRuntimeQueueReadModelSchema>;

export const CoachRuntimeServiceReadModelSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    sessionId: Type.String({ minLength: 1, maxLength: 64 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    assignmentType: CoachAssignmentTypeSchema,
    status: CoachServiceStatusSchema,
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    billingMode: CoachBillingModeSchema,
    minimumMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    billingStepMinutes: Type.Integer({ minimum: 1, maximum: 1_440 }),
    priceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    assignedAt: Type.String({ format: "date-time" }),
    startedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    version: Type.Integer({ minimum: 1 }),
    syncState: CoachRuntimeSyncStateSchema,
  },
  { additionalProperties: false },
);
export type CoachRuntimeServiceReadModel = Static<typeof CoachRuntimeServiceReadModelSchema>;

export const CoachAssignmentAvailabilityCodeSchema = Type.Union([
  Type.Literal("AVAILABLE"),
  Type.Literal("QUEUED"),
  Type.Literal("SERVING"),
  Type.Literal("PAUSED"),
  Type.Literal("CHECKED_OUT"),
  Type.Literal("NOT_CHECKED_IN"),
  Type.Literal("PROFILE_DISABLED"),
]);
export type CoachAssignmentAvailabilityCode = Static<typeof CoachAssignmentAvailabilityCodeSchema>;

export const CoachAssignmentAvailabilityReadModelSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    code: CoachAssignmentAvailabilityCodeSchema,
    label: Type.String({ minLength: 1, maxLength: 32 }),
    requestedAssignable: Type.Boolean(),
    requestedReason: Type.Union([Type.String({ minLength: 1, maxLength: 200 }), Type.Null()]),
    rotationAssignable: Type.Boolean(),
    rotationReason: Type.Union([Type.String({ minLength: 1, maxLength: 200 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type CoachAssignmentAvailabilityReadModel = Static<
  typeof CoachAssignmentAvailabilityReadModelSchema
>;

export const CoachSessionStopPreviewServiceSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Union([Type.Literal("ASSIGNED"), Type.Literal("ACTIVE")]),
    startedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    estimatedBilledMinutes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    estimatedServiceFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type CoachSessionStopPreviewService = Static<typeof CoachSessionStopPreviewServiceSchema>;

export const CoachSessionStopPreviewSchema = Type.Object(
  {
    sessionId: Type.String({ minLength: 1, maxLength: 64 }),
    tableId: Type.String({ minLength: 1, maxLength: 64 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    previewedAt: Type.String({ format: "date-time" }),
    serviceSetToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    services: Type.Array(CoachSessionStopPreviewServiceSchema, { maxItems: 100_000 }),
    estimatedServiceFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type CoachSessionStopPreview = Static<typeof CoachSessionStopPreviewSchema>;

export const CoachSessionStopCompletedServiceSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Union([Type.Literal("COMPLETED"), Type.Literal("CANCELLED")]),
    startedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    endedAt: Type.String({ format: "date-time" }),
    billedMinutes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    serviceFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    version: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type CoachSessionStopCompletedService = Static<
  typeof CoachSessionStopCompletedServiceSchema
>;

export const CoachServiceAssignmentOperatorResultSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    assignmentType: CoachAssignmentTypeSchema,
    status: Type.Literal("ACTIVE"),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    priceFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    pricingToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    assignedAt: Type.String({ format: "date-time" }),
    startedAt: Type.String({ format: "date-time" }),
    version: Type.Literal(2),
    syncState: CoachRuntimeSyncStateSchema,
  },
  { additionalProperties: false },
);
export type CoachServiceAssignmentOperatorResult = Static<
  typeof CoachServiceAssignmentOperatorResultSchema
>;

export const CoachRuntimeReadModelSchema = Type.Object(
  {
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    coachRotationEnabled: Type.Optional(Type.Boolean()),
    profiles: Type.Array(CoachProfilePublicSchema, { maxItems: 10_000 }),
    shifts: Type.Array(CoachRuntimeShiftReadModelSchema, { maxItems: 10_000 }),
    queue: Type.Array(CoachRuntimeQueueReadModelSchema, { maxItems: 10_000 }),
    services: Type.Array(CoachRuntimeServiceReadModelSchema, { maxItems: 100_000 }),
    assignmentAvailability: Type.Optional(
      Type.Array(CoachAssignmentAvailabilityReadModelSchema, { maxItems: 10_000 }),
    ),
    syncSummary: Type.Union([
      Type.Object(
        {
          pendingRecords: Type.Integer({ minimum: 0 }),
          conflictRecords: Type.Integer({ minimum: 0 }),
          reviewRecords: Type.Integer({ minimum: 0 }),
        },
        { additionalProperties: false },
      ),
      Type.Null(),
    ]),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachRuntimeReadModel = Static<typeof CoachRuntimeReadModelSchema>;

export const CoachQueuePolicySchema = Type.Object(
  {
    requestedAffectsRotation: Type.Boolean(),
    skippedMovesToTail: Type.Boolean(),
    passedMovesToTail: Type.Boolean(),
    completedRotationRequeue: Type.Union([
      Type.Literal("TAIL"),
      Type.Literal("KEEP_POSITION"),
      Type.Literal("MANUAL"),
    ]),
    version: Type.Integer({ minimum: 1 }),
    effectiveAt: Type.String({ format: "date-time" }),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachQueuePolicy = Static<typeof CoachQueuePolicySchema>;

export const CoachConfigurationSnapshotSchema = Type.Object(
  {
    version: Type.Integer({ minimum: 1 }),
    effectiveAt: Type.String({ format: "date-time" }),
    levels: Type.Array(CoachLevelSchema),
    profiles: Type.Array(CoachProfileSchema),
    serviceRules: Type.Array(CoachServiceRuleSchema),
    serviceRuleHistory: Type.Optional(Type.Array(CoachServiceRuleSchema)),
    priceOverrides: Type.Optional(Type.Array(CoachPriceOverrideSchema)),
    priceOverrideHistory: Type.Optional(Type.Array(CoachPriceOverrideSchema)),
    queuePolicy: CoachQueuePolicySchema,
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachConfigurationSnapshot = Static<typeof CoachConfigurationSnapshotSchema>;

const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const ReasonSchema = Type.String({ minLength: 2, maxLength: 500 });

export const PreviewCoachServiceReplacementRequestSchema = Type.Object(
  {
    replacementCoachId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedServiceVersion: Type.Integer({ minimum: 1 }),
    reason: ReasonSchema,
  },
  { additionalProperties: false },
);
export type PreviewCoachServiceReplacementRequest = Static<
  typeof PreviewCoachServiceReplacementRequestSchema
>;

export const CoachServiceReplacementPreviewReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal(1),
    serviceId: Type.String({ minLength: 1, maxLength: 64 }),
    replacementCoachId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedServiceVersion: Type.Integer({ minimum: 1 }),
    reason: ReasonSchema,
    effectiveAt: Type.String({ format: "date-time" }),
    expiresAt: Type.String({ format: "date-time" }),
    expectedEffectiveSeconds: Type.Integer({ minimum: 0 }),
    expectedBilledMinutes: Type.Integer({ minimum: 0 }),
    expectedServiceFeeFen: Type.Integer({ minimum: 0 }),
    expectedCommissionBaseFen: Type.Integer({ minimum: 0 }),
    expectedCommissionFen: Type.Integer({ minimum: 0 }),
    replacementServiceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    replacementRuleId: Type.String({ minLength: 1, maxLength: 64 }),
    replacementRuleVersion: Type.Integer({ minimum: 1 }),
    replacementPriceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    replacementPricingToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    replacementToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type CoachServiceReplacementPreviewReadModel = Static<
  typeof CoachServiceReplacementPreviewReadModelSchema
>;

export const CoachServiceStopRequestStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("COMPLETED"),
  Type.Literal("CANCELLED"),
]);
export type CoachServiceStopRequestStatus = Static<typeof CoachServiceStopRequestStatusSchema>;

export const CoachServiceStopRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal(1),
    requestId: Type.String({ minLength: 1, maxLength: 64 }),
    requestNumber: Type.String({ pattern: "^XZ-[0-9]{8}-[0-9A-F]{12}$" }),
    serviceId: Type.String({ minLength: 1, maxLength: 64 }),
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    serviceVersionAtRequest: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: ReasonSchema,
    status: CoachServiceStopRequestStatusSchema,
    requestedAt: Type.String({ format: "date-time" }),
    resolvedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    resolvedByDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    resolutionNote: Type.Union([ReasonSchema, Type.Null()]),
    completedServiceVersion: Type.Union([
      Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type CoachServiceStopRequest = Static<typeof CoachServiceStopRequestSchema>;

export const CoachServiceStopRequestListSchema = Type.Object(
  {
    contractVersion: Type.Literal(1),
    items: Type.Array(CoachServiceStopRequestSchema, { maxItems: 200 }),
    terminalHistoryTruncated: Type.Boolean(),
    generatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CoachServiceStopRequestList = Static<typeof CoachServiceStopRequestListSchema>;

export const RequestCoachServiceStopRequestSchema = Type.Object(
  {
    expectedServiceVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RequestCoachServiceStopRequest = Static<typeof RequestCoachServiceStopRequestSchema>;

export const SettleCoachCommissionMonthRequestSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1, maxLength: 64 }),
    month: Type.String({ pattern: "^\\d{4}-\\d{2}$" }),
    reason: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type SettleCoachCommissionMonthRequest = Static<
  typeof SettleCoachCommissionMonthRequestSchema
>;

export const CoachCheckInRequestSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1 }),
    reason: Type.Optional(Type.Union([ReasonSchema, Type.Null()])),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CoachCheckInRequest = Static<typeof CoachCheckInRequestSchema>;

export const CoachCheckOutRequestSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1 }),
    reason: Type.Union([ReasonSchema, Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CoachCheckOutRequest = Static<typeof CoachCheckOutRequestSchema>;

export const CoachQueueActionRequestSchema = Type.Object(
  {
    coachId: Type.String({ minLength: 1 }),
    action: Type.Union([
      Type.Literal("JOIN"),
      Type.Literal("PAUSE"),
      Type.Literal("RESUME"),
      Type.Literal("SKIP"),
      Type.Literal("PASS"),
      Type.Literal("MOVE"),
    ]),
    targetPosition: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    reason: Type.Union([ReasonSchema, Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CoachQueueActionRequest = Static<typeof CoachQueueActionRequestSchema>;

export const AssignCoachServiceRequestSchema = Type.Object(
  {
    sessionId: Type.String({ minLength: 1 }),
    coachId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    expectedCoachId: Type.String({ minLength: 1 }),
    assignmentType: CoachAssignmentTypeSchema,
    serviceRuleId: Type.String({ minLength: 1 }),
    expectedPriceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    pricingToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    previewToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    customerRequestEventId: Type.Optional(Type.String({ minLength: 8, maxLength: 128 })),
    reason: Type.Optional(Type.Union([ReasonSchema, Type.Null()])),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type AssignCoachServiceRequest = Static<typeof AssignCoachServiceRequestSchema>;

export const TransitionCoachServiceRequestSchema = Type.Object(
  {
    action: Type.Union([
      Type.Literal("START"),
      Type.Literal("COMPLETE"),
      Type.Literal("END_EARLY"),
      Type.Literal("CANCEL"),
      Type.Literal("TERMINATE_ABNORMAL"),
    ]),
    reason: Type.Union([ReasonSchema, Type.Null()]),
    stopRequestId: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    ),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type TransitionCoachServiceRequest = Static<typeof TransitionCoachServiceRequestSchema>;

export const ReplaceCoachServiceRequestSchema = Type.Object(
  {
    replacementCoachId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedServiceVersion: Type.Integer({ minimum: 1 }),
    reason: ReasonSchema,
    effectiveAt: Type.String({ format: "date-time" }),
    previewExpiresAt: Type.String({ format: "date-time" }),
    expectedEffectiveSeconds: Type.Integer({ minimum: 0 }),
    expectedBilledMinutes: Type.Integer({ minimum: 0 }),
    expectedServiceFeeFen: Type.Integer({ minimum: 0 }),
    expectedCommissionBaseFen: Type.Integer({ minimum: 0 }),
    expectedCommissionFen: Type.Integer({ minimum: 0 }),
    replacementRuleId: Type.String({ minLength: 1, maxLength: 64 }),
    replacementRuleVersion: Type.Integer({ minimum: 1 }),
    replacementPriceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    replacementPricingToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    replacementToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReplaceCoachServiceRequest = Static<typeof ReplaceCoachServiceRequestSchema>;

export const AdjustCoachServiceRequestSchema = Type.Object(
  {
    amountFen: Type.Integer({ maximum: -1 }),
    reason: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type AdjustCoachServiceRequest = Static<typeof AdjustCoachServiceRequestSchema>;

export const CoachConfigurationImportRequestSchema = Type.Object(
  {
    mode: Type.Union([Type.Literal("PREVIEW"), Type.Literal("APPLY")]),
    configuration: CoachConfigurationSnapshotSchema,
    expectedPersonAggregateVersions: Type.Optional(
      Type.Record(Type.String({ pattern: "^.{1,64}$" }), Type.Integer({ minimum: 1 }), {
        maxProperties: 500,
      }),
    ),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CoachConfigurationImportRequest = Static<typeof CoachConfigurationImportRequestSchema>;

export const CoachConfigurationImportErrorSchema = Type.Object(
  {
    path: Type.String({ minLength: 1, maxLength: 200 }),
    code: Type.String({ minLength: 1, maxLength: 80 }),
    message: Type.String({ minLength: 1, maxLength: 500 }),
  },
  { additionalProperties: false },
);
export type CoachConfigurationImportError = Static<typeof CoachConfigurationImportErrorSchema>;

const CoachConfigurationImportCountsSchema = Type.Object(
  {
    levels: Type.Integer({ minimum: 0 }),
    profiles: Type.Integer({ minimum: 0 }),
    serviceRules: Type.Integer({ minimum: 0 }),
    queuePolicies: Type.Integer({ minimum: 0 }),
    priceOverrides: Type.Optional(Type.Integer({ minimum: 0 })),
  },
  { additionalProperties: false },
);

export const CoachConfigurationImportResultSchema = Type.Object(
  {
    idempotencyKey: IdempotencyKeySchema,
    mode: Type.Union([Type.Literal("PREVIEW"), Type.Literal("APPLY")]),
    valid: Type.Boolean(),
    applied: Type.Boolean(),
    imported: CoachConfigurationImportCountsSchema,
    unchanged: CoachConfigurationImportCountsSchema,
    configurationVersion: Type.Integer({ minimum: 1 }),
    errors: Type.Array(CoachConfigurationImportErrorSchema),
    appliedAt: Type.String({ format: "date-time" }),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachConfigurationImportResult = Static<typeof CoachConfigurationImportResultSchema>;

export const CoachProfileAccountOnboardingRequestSchema = Type.Object(
  {
    profileId: Type.String({ minLength: 1, maxLength: 64 }),
    configuration: CoachConfigurationSnapshotSchema,
    account: Type.Object(
      {
        login: Type.String({ minLength: 3, maxLength: 64 }),
        displayName: Type.String({ minLength: 1, maxLength: 64 }),
        password: Type.String({ minLength: 8, maxLength: 128 }),
      },
      { additionalProperties: false },
    ),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CoachProfileAccountOnboardingRequest = Static<
  typeof CoachProfileAccountOnboardingRequestSchema
>;

export const CoachProfileAccountOnboardingResultSchema = Type.Object(
  {
    idempotencyKey: IdempotencyKeySchema,
    status: Type.Union([
      Type.Literal("SUCCEEDED"),
      Type.Literal("FAILED"),
      Type.Literal("COMPENSATED_FAILURE"),
      Type.Literal("RECOVERY_REQUIRED"),
    ]),
    profileId: Type.String({ minLength: 1, maxLength: 64 }),
    account: Type.Union([
      Type.Object(
        {
          id: Type.String({ minLength: 1 }),
          tenantId: Type.String({ minLength: 1 }),
          storeId: Type.String({ minLength: 1 }),
          login: Type.String({ minLength: 3, maxLength: 64 }),
          displayName: Type.String({ minLength: 1, maxLength: 64 }),
          role: Type.Literal("COACH"),
          coachProfileId: Type.String({ minLength: 1, maxLength: 64 }),
          activationStatus: Type.Literal("ACTIVE"),
          createdAt: Type.String({ format: "date-time" }),
          updatedAt: Type.String({ format: "date-time" }),
        },
        { additionalProperties: false },
      ),
      Type.Null(),
    ]),
    message: Type.String({ minLength: 1, maxLength: 500 }),
    retryable: Type.Boolean(),
    configurationVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    compensation: Type.Union([
      Type.Object(
        {
          profileStatus: Type.Literal("DISABLED"),
          profileVersion: Type.Integer({ minimum: 2 }),
          recorded: Type.Boolean(),
        },
        { additionalProperties: false },
      ),
      Type.Null(),
    ]),
    audit: Type.Object(
      {
        profileConfiguration: Type.Union([
          Type.Literal("NOT_WRITTEN"),
          Type.Literal("RECORDED"),
          Type.Literal("COMPENSATED"),
          Type.Literal("RECOVERY_REQUIRED"),
        ]),
        operatorAccount: Type.Union([Type.Literal("NOT_WRITTEN"), Type.Literal("RECORDED")]),
        onboarding: Type.Literal("RECORDED"),
      },
      { additionalProperties: false },
    ),
    evidence: CoachEvidenceSchema,
  },
  { additionalProperties: false },
);
export type CoachProfileAccountOnboardingResult = Static<
  typeof CoachProfileAccountOnboardingResultSchema
>;
